import * as React from "react";
import * as H from "history";
import { toast } from "react-toastify";
import { CellInfo } from "react-table";
import * as _ from "lodash";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import {
  ITableInstance,
  BulkSelectState
} from "src/library/components/Tables/Table";
import { Table } from "src/library/components/Tables";
import { ActionButton } from "src/library/components/Button";
import { IAction } from "src/library/components/Button/ActionButton";
import { ISupportTicket } from "src/modules/support/api/support.api";
import {
  FilterParams,
  PaginatedParams
} from "src/core/shared/api";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";

import { connectorOrderExportIssuesTableColumns } from "../tables/connectorOrderExportIssueTableColumns";
import {
  IAPIOrderWithProcessJobError,
  useConnectorApi
} from "../api/connector.api";

interface ViewAdminSupportTicketsTabProps {
  history: H.History;
  terminology: ITerminology;
}

const defaultPagination: PaginatedParams<IAPIOrderWithProcessJobError> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "orderDeliveryDate", "ASC" ] ]
};

/**
 * Tab for everything related to api order exports
 */
const APIOrderExportTab: React.ReactFCWithChildren<ViewAdminSupportTicketsTabProps> = ({ history, terminology }) => {
  const TableRef = React.useRef<ITableInstance<ISupportTicket>>(null);
  const [ { globalSettings } ] = useGlobalSettings();
  const [ listOrdersWithIssuesRes, listOrdersWithIssuesReq ] = useConnectorApi("LIST_ORDER_EXPORT_ISSUES");
  const [ retryApiJobRes, retryApiJobReq ] = useConnectorApi("RETRY_API_JOB");
  const [ bulkHandleApiIssueRes, bulkHandleApiIssueReq ] = useConnectorApi("BULK_HANDLE_API_ISSUE");

  /**
   * Table actions
   */
  const tableActions = React.useCallback((row: CellInfo) => {
    const actions: IAction[] = [
      {
        title: `Retry ${globalSettings?.erp.name} export`,
        action: () => retryApiJobReq({ processJobId: row.original.processJobId })
      },
      {
        title: `View ${terminology.order.singular.text}`,
        action: () => history.push(`/orders/${row.original.id}`),
        newTabPath: `/orders/${row.original.id}`
      }
    ];

    return actions;
  }, [
    history,
    retryApiJobReq,
    terminology.order.singular.text
  ]);

  React.useEffect(() => {
    if (bulkHandleApiIssueRes.data) {
      if (bulkHandleApiIssueRes.data.failed === 0) {
        toast.success("Bulk action successful and is being processed");
      } else {
        if (bulkHandleApiIssueRes.data.queued === 0) {
          toast.error("None of the selected issues could be resolved");
        } else {
          toast.warn("Some of the selected issues failed to be processed");
        }
      }

      if (TableRef.current) {
        TableRef.current.reloadTable();
        TableRef.current.resetPagination();
      }
    } else if (bulkHandleApiIssueRes.error) {
      toast.error(bulkHandleApiIssueRes.error.message);
    }
  }, [ bulkHandleApiIssueRes.data, bulkHandleApiIssueRes.error ]);

  /**
   * Function to define bulk order actions
   */
  const bulkActionOptions = React.useCallback((bulkSelectData: BulkSelectState) => {
    return [
      {
        title: "Manually export as CSV",
        action: () => bulkHandleApiIssueReq({
          action: "export",
          processJobIds: _.values(bulkSelectData)
        })
      },
      {
        title: `Retry ${globalSettings?.erp.name} export`,
        action: () => bulkHandleApiIssueReq({
          action: "retry",
          processJobIds: _.values(bulkSelectData)
        })
      },
      {
        title: "Manually resolved",
        action: () => bulkHandleApiIssueReq({
          action: "ignore",
          processJobIds: _.values(bulkSelectData)
        })
      }
    ] as IAction[];
  }, [ bulkHandleApiIssueReq ]);

  /**
   * Fetches table data
   */

  const onFetchData = React.useCallback((filters: FilterParams<IAPIOrderWithProcessJobError>, pagination: PaginatedParams<IAPIOrderWithProcessJobError>) => {
    // Fetch support tickets
    listOrdersWithIssuesReq({
      filters,
      pagination
    });
  }, [ listOrdersWithIssuesReq ]);

  // listSupportTicketsRes change
  React.useEffect(() => {
    // If an error occurs show a toast
    if (listOrdersWithIssuesRes.error) {
      toast.error(listOrdersWithIssuesRes.error.message);
    }
  }, [ listOrdersWithIssuesRes.error ]);

  return (
    <>
      <Table
        tableId="apiExportIssues"
        ref={TableRef}
        loading={listOrdersWithIssuesRes.pending || retryApiJobRes.pending}
        tableOf={`${_.capitalize(globalSettings?.erp.name)} Export Issues`}
        view="list"
        defaultPagination={defaultPagination}
        // Map the process job id as the main id for the table to use
        data={listOrdersWithIssuesRes.data ? _.map(listOrdersWithIssuesRes.data.result, order => ({
          ...order,
          processJobId: order.processJob?.id
        })) : []}
        onFetchData={onFetchData}
        pages={listOrdersWithIssuesRes.data ? listOrdersWithIssuesRes.data.pages : 0}
        totalRows={listOrdersWithIssuesRes.data ? listOrdersWithIssuesRes.data.count : 0}
        bulkActionOptions={bulkSelectData => bulkActionOptions(bulkSelectData)}
        bulkActionsLoading={bulkHandleApiIssueRes.pending}
        bulkActionsIdColumn="processJobId"
        columns={connectorOrderExportIssuesTableColumns({
          ActionButton: (row: any) => {
            const actions = tableActions(row);

            return actions.length ? (
              <ActionButton
                title="actions"
                items={tableActions(row)}
              />
            ) : null;
          }
        })}
      />
    </>
  );
};

export default APIOrderExportTab;
